/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseTicket.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ProjectVariantDialog from "../../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";
import ProjectVariantSearchbar from "../../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RaiseEETicket from "./RaiseEETicket";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import { HandleApiActions } from "../../../redux/actions";
import RaiseSoftwareTickets from "./RaiseSoftwareTickets";
import RaiseVehicleTickets from "./RaiseVehicleTickets";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";

const TICKET_COMPONENT_BY_TAB = {
  0: "vehicle-system",
  1: "vehicle-component",
  2: "ee",
  3: "swm",
};

const replaceUpdatedTestCaseIds = (testCaseIds, updatedTestCaseIdMap) =>
  (testCaseIds || []).map((testCaseId) => {
    const updatedTestCaseId = updatedTestCaseIdMap?.get(String(testCaseId));
    return updatedTestCaseId || testCaseId;
  });

const formatVsFirstLinkage = (firstLinkage, updatedTestCaseIdMap) => {
  const { addOn, legal, test_cases, ...restLinkage } = firstLinkage;

  const sanitizedLinkage = {
    ...restLinkage,
    ...(Array.isArray(restLinkage.test_case_ids)
      ? {
        test_case_ids: replaceUpdatedTestCaseIds(
          restLinkage.test_case_ids,
          updatedTestCaseIdMap,
        ),
      }
      : {}),
  };

  delete sanitizedLinkage.vs_id;
  delete sanitizedLinkage.vs_name;
  delete sanitizedLinkage.vc_to_legal;
  delete sanitizedLinkage.ee_id;

  const vehicleSystems = Array.isArray(addOn) ? addOn : [addOn];

  sanitizedLinkage.entities = vehicleSystems.map((item) => {
    const vcData = Array.isArray(item.vcData) ? item.vcData : [];
    const vsEntity = {
      id: Number(item.id || item.vs_id),
      supplier_name: item.selected_supplier || "",
    };
    if (vcData.length > 0) {
      vsEntity.child_entities = vcData.map((vc) => ({
        id: Number(vc.id),
        supplier_name: vc.selected_supplier || "",
      }));
    }
    return vsEntity;
  });

  const legalItem = {
    lr_ids: sanitizedLinkage.lr_ids || [],
    description: sanitizedLinkage.description || "",
    oem_users: sanitizedLinkage.oem_users || [],
    tech_service_users: sanitizedLinkage.tech_service_users || [],
    suppliers: sanitizedLinkage.suppliers || [],
    test_case_ids: sanitizedLinkage.test_case_ids || [],
    ...(sanitizedLinkage.dynamic_form ? { dynamic_form: sanitizedLinkage.dynamic_form } : {}),
  };

  const formattedLinkage = {
    ...sanitizedLinkage,
    legal: Array.isArray(legal) && legal.length > 0 ? legal : [legalItem],
  };

  delete formattedLinkage.lr_ids;
  delete formattedLinkage.description;
  delete formattedLinkage.oem_users;
  delete formattedLinkage.tech_service_users;
  delete formattedLinkage.suppliers;
  delete formattedLinkage.test_case_ids;
  delete formattedLinkage.dynamic_form;

  return [formattedLinkage];
};

const resolveEeEntities = (addOn) =>
  addOn.map((ee) => ({
    id: Number(ee.ee_id),
    supplier_name: ee.selected_supplier || "",
  }));

const resolveSwmEntities = (addOn) =>
  addOn.map((swm) => ({
    id: Number(swm.swm_id),
    supplier_name: swm.selected_supplier || "",
  }));

const mapVcEntities = (vcList) => {
  if (!Array.isArray(vcList)) {
    return [];
  }
  return vcList.map((vc) => ({
    id: Number(vc.id),
    supplier_name: vc.selected_supplier || "",
  }));
};

const resolveVsEntities = (restLinkage, addOn, entityType) => {
  const vsId = Number(restLinkage.vs_id);
  const supplierName = addOn.selected_supplier || "";

  if (entityType === "vs") {
    const childEntities = mapVcEntities(addOn.vcData);
    const vsEntity = {
      id: vsId,
      supplier_name: supplierName,
    };
    if (childEntities.length > 0) {
      vsEntity.child_entities = childEntities;
    }
    return [vsEntity];
  }

  if (restLinkage.vc_to_legal) {
    return mapVcEntities(addOn.vcData);
  }

  return [
    {
      id: vsId,
      supplier_name: supplierName,
    },
  ];
};

const resolveLinkageEntities = (restLinkage, addOn, entityType) => {
  if (!addOn) {
    return undefined;
  }
  if (restLinkage.ee_ids) {
    return resolveEeEntities(addOn);
  }
  if (restLinkage.swm_ids) {
    return resolveSwmEntities(addOn);
  }
  if (restLinkage.vs_id) {
    return resolveVsEntities(restLinkage, addOn, entityType);
  }
  return undefined;
};

const formatLinkageLegal = (singleLinkage, legal, updatedTestCaseIdMap) => {
  if (!Array.isArray(legal)) {
    const legalItem = {
      lr_ids: singleLinkage.lr_ids || [],
      description: singleLinkage.description || "",
      oem_users: singleLinkage.oem_users || [],
      tech_service_users: singleLinkage.tech_service_users || [],
      suppliers: singleLinkage.suppliers || [],
      test_case_ids: singleLinkage.test_case_ids || [],
      ...(singleLinkage.dynamic_form ? { dynamic_form: singleLinkage.dynamic_form } : {}),
    };
    const formattedLinkage = {
      ...singleLinkage,
      legal: [legalItem],
    };
    delete formattedLinkage.lr_ids;
    delete formattedLinkage.description;
    delete formattedLinkage.oem_users;
    delete formattedLinkage.tech_service_users;
    delete formattedLinkage.suppliers;
    delete formattedLinkage.test_case_ids;
    delete formattedLinkage.dynamic_form;
    return formattedLinkage;
  }

  return {
    ...singleLinkage,
    legal: legal.map((legalItem) => {
      const { test_cases, ...restLegalItem } = legalItem;
      return {
        ...restLegalItem,
        ...(Array.isArray(restLegalItem.test_case_ids)
          ? {
            test_case_ids: replaceUpdatedTestCaseIds(
              restLegalItem.test_case_ids,
              updatedTestCaseIdMap,
            ),
          }
          : {}),
      };
    }),
  };
};

const sanitizeSingleLinkage = (linkage, updatedTestCaseIdMap, entityType) => {
  const { addOn, legal, test_cases, ...restLinkage } = linkage;
  const sanitizedLinkage = {
    ...restLinkage,
    ...(Array.isArray(restLinkage.test_case_ids)
      ? {
        test_case_ids: replaceUpdatedTestCaseIds(
          restLinkage.test_case_ids,
          updatedTestCaseIdMap,
        ),
      }
      : {}),
  };

  delete sanitizedLinkage.ee_id;

  const entities = resolveLinkageEntities(restLinkage, addOn, entityType);
  if (entities) {
    sanitizedLinkage.entities = entities;
  }

  if (restLinkage.ee_ids && addOn) {
    delete sanitizedLinkage.ee_ids;
  }
  if (restLinkage.swm_ids && addOn) {
    delete sanitizedLinkage.swm_ids;
  }
  delete sanitizedLinkage.vs_id;
  delete sanitizedLinkage.vs_name;
  delete sanitizedLinkage.vc_to_legal;

  return formatLinkageLegal(sanitizedLinkage, legal, updatedTestCaseIdMap);
};

const isBothVsAndVcSelected = (linkages = []) => {
  if (!Array.isArray(linkages) || linkages.length === 0) return false;
  return linkages.some((linkage) => {
    const items = Array.isArray(linkage.addOn)
      ? linkage.addOn
      : linkage.addOn
      ? [linkage.addOn]
      : [];
    const hasVs = Boolean(
      linkage.vs_id || items.some((item) => item?.id || item?.vs_id),
    );
    const hasVc =
      items.some(
        (item) => Array.isArray(item?.vcData) && item.vcData.length > 0,
      ) ||
      (Array.isArray(linkage.vcData) && linkage.vcData.length > 0) ||
      Boolean(linkage.vc_to_legal);
    return hasVs && hasVc;
  });
};

const sanitizeLinkagesForTicketCreation = (
  updatedTestCaseIdMap,
  linkages = [],
  entityType = "",
) => {
  if (entityType === "vs" && linkages.length > 0) {
    return linkages.flatMap((linkage) =>
      formatVsFirstLinkage(linkage, updatedTestCaseIdMap),
    );
  }

  return linkages.map((linkage) =>
    sanitizeSingleLinkage(linkage, updatedTestCaseIdMap, entityType),
  );
};

function RaiseTicket() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const currentLocation = useLocation();
  const urlParams = new URLSearchParams(currentLocation.search);
  const tabFromURL = urlParams.get("tab");
  const [savedEELinkages, setSavedEELinkages] = useState([]);
  const [savedSWMLinkages, setSavedSWMLinkages] = useState([]);
  const [savedVehicleSystemLinkages, setSavedVehicleSystemLinkages] = useState(
    [],
  );
  const [savedVehicleComponentLinkages, setSavedVehicleComponentLinkages] =
    useState([]);

  const [ticketComponent, setTicketComponent] = useState("vehicle-system");
  const [disableRaiseTicketButton, setDisableRaiseTicketButton] =
    useState(true);

  const [currentTabValue, setCurrentTabValue] = useState(0);

  const getTicketComponentByTab = (tabIndex) =>
    TICKET_COMPONENT_BY_TAB[Number(tabIndex)] || TICKET_COMPONENT_BY_TAB[0];

  const getTabState = (tabIndex) => {
    const tabNumber = Number(tabIndex);
    const linkagesByTab = {
      0: savedVehicleSystemLinkages,
      1: savedVehicleComponentLinkages,
      2: savedEELinkages,
      3: savedSWMLinkages,
    };

    return {
      component: getTicketComponentByTab(tabNumber),
      linkages: linkagesByTab[tabNumber] || savedVehicleSystemLinkages,
    };
  };



  const [isLoading, setIsLoading] = useState(false);

  const handleLinkEEToLRs = async ({ draft }) => {
    let apiDetails = typeApprovalAPI.add_ee_intersection;
    let intersectionDetails = sanitizeLinkagesForTicketCreation(
      new Map(),
      savedEELinkages,
    );
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedVariantDetails?.variant_id),
            intersections: intersectionDetails,
            is_save_draft: draft,
            current_time: new Date().toISOString(),
          },
          show_toast: true,
        }),
      ),
    );
    if (!resp.error) {
      setSavedEELinkages([]);
      if (draft) {
        navigate("/dashboard/v2/type-approval/intersection?tab=2");
      } else {
        navigate("/dashboard/v2/type-approval/tickets?tab=2");
      }
    }
  };

  const handleLinkSWMToLRs = async ({ draft }) => {
    let apiDetails = typeApprovalAPI.add_intersection;
    let intersectionDetails = sanitizeLinkagesForTicketCreation(
      new Map(),
      savedSWMLinkages,
    );
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedVariantDetails?.variant_id),
            intersections: intersectionDetails,
            is_save_draft: draft,
            current_time: new Date().toISOString(),
          },
          show_toast: true,
        }),
      ),
    );
    if (!resp.error) {
      setSavedSWMLinkages([]);
      if (draft) {
        navigate("/dashboard/v2/type-approval/intersection?tab=3");
      } else {
        navigate("/dashboard/v2/type-approval/tickets?tab=3");
      }
    }
  };

  const handleLinkVSToLRs = async ({
    draft,
    linkages,
    apiDetails = typeApprovalAPI?.get_vehicle_add_intersection,
    redirectTab = 0,
    entityType = "vs",
  }) => {
    let intersectionDetails = sanitizeLinkagesForTicketCreation(
      new Map(),
      linkages,
      entityType,
    );
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedVariantDetails?.variant_id),
            intersections: intersectionDetails,
            is_save_draft: draft,
            current_time: new Date().toISOString(),
          },
          show_toast: true,
        }),
      ),
    );
    if (!resp.error) {
      setSavedVehicleSystemLinkages([]);
      setSavedVehicleComponentLinkages([]);
      if (draft) {
        navigate(`/dashboard/v2/type-approval/intersection?tab=${redirectTab}`);
      } else {
        navigate(`/dashboard/v2/type-approval/tickets?tab=${redirectTab}`);
      }
    }
  };

  useEffect(() => {
    const nextTab = Number(tabFromURL) || 0;
    setCurrentTabValue(nextTab);
    setTicketComponent(getTicketComponentByTab(nextTab));
    setDisableRaiseTicketButton(true);
  }, [tabFromURL]);
  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Regulatory Compliance" },
              { label: "Link & Unlink" },
            ]}
          />
        </Grid>

        <Grid container sx={{ height: "100%", alignItems: "center", p: 2 }}>
          <Grid item sm={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box>
                <ProjectVariantSearchbar />
              </Box>

              <Box>
                <ProjectVariantDialog />
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <CustomButtonV2
                  btnName="Back"
                  startIcon={<ArrowBackIcon />}
                  handleOnClick={() =>
                    navigate("/dashboard/v2/type-approval/link")
                  }
                />
              </Box>
              <Box sx={{ flexGrow: 1 }} />

            </Box>
          </Grid>
        </Grid>
        <Grid item sm={12} sx={{ p: 2 }}>
          <CustomTabsV2
            key={currentTabValue} // Force re-render when tab changes
            centerTabs={true}
            defaultTab={currentTabValue}
            onTabChange={(tabIndex) => {
              const tabState = getTabState(tabIndex);
              setCurrentTabValue(tabIndex);
              setTicketComponent(tabState.component);
              setDisableRaiseTicketButton(tabState.linkages.length === 0);
            }}
            renderComponentBelow={
              <Grid item sm={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    pr: 3,
                  }}
                >
                  {selectedProjectDetails?.parent_project_id &&
                    selectedVariantDetails?.parent_variant_details
                      ?.variant_id && (
                      <CustomButtonV2
                        btnName="Recreate Ticket"
                        disabled={isLoading}
                        handleOnClick={async () => {
                          let ticketType;
                          switch (ticketComponent) {
                            case "ee":
                              ticketType = 2;
                              break;
                            case "swm":
                              ticketType = 1;
                              break;
                            case "vehicle":
                            case "vehicle-system":
                            case "vehicle-component":
                              ticketType = 3;
                              break;
                            default:
                              break;
                          }

                          setIsLoading(true);
                          await Promise.resolve(
                            dispatch(
                              HandleApiActions({
                                ...typeApprovalAPI.recreate_parent_tickets,
                                params: {
                                  ticket_type: ticketType,
                                  project_id: selectedProjectDetails?.project_id,
                                  variant_id: selectedVariantDetails?.variant_id,
                                  parent_project_id:
                                    selectedProjectDetails?.parent_project_id,
                                  parent_variant_id:
                                    selectedVariantDetails?.parent_variant_details
                                      ?.variant_id,
                                },
                                show_toast: true,
                              }),
                            ),
                          );
                          setIsLoading(false);
                        }}
                      />
                    )}

                  <CustomButtonV2
                    btnName="Save Draft"
                    disabled={disableRaiseTicketButton}
                    handleOnClick={() => {
                      switch (ticketComponent) {
                        case "ee":
                          handleLinkEEToLRs({ draft: true });
                          break;
                        case "swm":
                          handleLinkSWMToLRs({ draft: true });
                          break;
                        case "vehicle":
                        case "vehicle-system":
                          handleLinkVSToLRs({
                            draft: true,
                            linkages: savedVehicleSystemLinkages,
                            apiDetails:
                              typeApprovalAPI?.get_vehicle_systems_add_intersection,
                            redirectTab: 0,
                            entityType: "vs",
                          });
                          break;
                        case "vehicle-component": {
                          const bothSelected = isBothVsAndVcSelected(
                            savedVehicleComponentLinkages,
                          );
                          handleLinkVSToLRs({
                            draft: true,
                            linkages: savedVehicleComponentLinkages,
                            apiDetails: bothSelected
                              ? typeApprovalAPI?.get_vehicle_systems_add_intersection
                              : typeApprovalAPI?.get_vehicle_add_intersection,
                            redirectTab: bothSelected ? 0 : 1,
                            entityType: bothSelected ? "vs" : "vc",
                          });
                          break;
                        }
                        default:
                          break;
                      }
                    }}
                    type="blue-outline"
                  />
                  <CustomButtonV2
                    btnName="Raise Ticket"
                    disabled={disableRaiseTicketButton}
                    handleOnClick={() => {
                      switch (ticketComponent) {
                        case "ee":
                          handleLinkEEToLRs({ draft: false });
                          break;
                        case "swm":
                          handleLinkSWMToLRs({ draft: false });
                          break;
                        case "vehicle":
                        case "vehicle-system":
                          handleLinkVSToLRs({
                            draft: false,
                            linkages: savedVehicleSystemLinkages,
                            apiDetails:
                              typeApprovalAPI?.get_vehicle_systems_add_intersection,
                            redirectTab: 0,
                            entityType: "vs",
                          });
                          break;
                        case "vehicle-component": {
                          const bothSelected = isBothVsAndVcSelected(
                            savedVehicleComponentLinkages,
                          );
                          handleLinkVSToLRs({
                            draft: false,
                            linkages: savedVehicleComponentLinkages,
                            apiDetails: bothSelected
                              ? typeApprovalAPI?.get_vehicle_systems_add_intersection
                              : typeApprovalAPI?.get_vehicle_add_intersection,
                            redirectTab: bothSelected ? 0 : 1,
                            entityType: bothSelected ? "vs" : "vc",
                          });
                          break;
                        }
                        default:
                          break;
                      }
                    }}
                  />
                </Box>
              </Grid>
            }
            tabs={[
              {
                name: "Vehicle System",
                count: "",
                content: (
                  <RaiseVehicleTickets
                    savedLinkages={savedVehicleSystemLinkages}
                    entityType={"vehicle"}
                    vehicleTicketMode="system"
                    setSavedLinkages={(data) => {
                      setSavedVehicleSystemLinkages(data);
                      setDisableRaiseTicketButton(data.length === 0);
                      setTicketComponent("vehicle-system");
                    }}
                  />
                ),
              },
              {
                name: "Vehicle Component",
                count: "",
                content: (
                  <RaiseVehicleTickets
                    savedLinkages={savedVehicleComponentLinkages}
                    entityType={"vehicle"}
                    vehicleTicketMode="component"
                    setSavedLinkages={(data) => {
                      console.log("--- RaiseTicket.jsx: setSavedLinkages for Vehicle Component called with data:", data);
                      setSavedVehicleComponentLinkages(data);
                      setDisableRaiseTicketButton(data.length === 0);
                      setTicketComponent("vehicle-component");
                    }}
                  />
                ),
              },
              {
                name: "EE Component",
                count: "",
                content: (
                  <RaiseEETicket
                    savedLinkages={savedEELinkages}
                    entityType={"ee"}
                    setSavedLinkages={(data) => {
                      setSavedEELinkages(data);
                      setDisableRaiseTicketButton(data.length === 0);
                      setTicketComponent("ee");
                    }}
                  />
                ),
              },
              {
                name: "Software Module",
                count: "",
                content: (
                  <RaiseSoftwareTickets
                    savedLinkages={savedSWMLinkages}
                    entityType={"swm"}
                    setSavedLinkages={(data) => {
                      setSavedSWMLinkages(data);
                      setDisableRaiseTicketButton(data.length === 0);
                      setTicketComponent("swm");
                    }}
                  />
                ),
              },
            ]}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default RaiseTicket;
